import React, { useState, useEffect } from 'react';
import { FiSettings, FiSave, FiPercent, FiDollarSign, FiEdit2, FiCheckCircle, FiCheck } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RoomSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gstPercentage, setGstPercentage] = useState(18);
  const [isGstInclude, setIsGstInclude] = useState(false);

  // Fetch room settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vendor/room-management/settings');
      
      if (response.data.success) {
        setGstPercentage(response.data.settings.gstPercentage);
        setIsGstInclude(response.data.settings.isGstInclude || false);
      }
    } catch (error) {
      console.error('Error fetching room settings:', error);
      toast.error('Failed to fetch room settings');
    } finally {
      setLoading(false);
    }
  };

  // Update room settings
  const updateSettings = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const response = await api.put('/vendor/room-management/settings', { 
        gstPercentage, 
        isGstInclude 
      });
      
      if (response.data.success) {
        toast.success('Room settings updated successfully!');
      }
    } catch (error) {
      console.error('Error updating room settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update room settings');
    } finally {
      setSaving(false);
    }
  };

  // Calculate total with GST
  const calculateTotalWithGST = (baseAmount) => {
    if (isGstInclude) {
      // If GST is included, the base amount already includes GST
      // So we need to extract the GST amount and show the breakdown
      const total = baseAmount;
      const gstAmount = (total * gstPercentage) / (100 + gstPercentage);
      const actualBase = total - gstAmount;
      return {
        total: total.toFixed(2),
        gstAmount: gstAmount.toFixed(2),
        baseAmount: actualBase.toFixed(2)
      };
    } else {
      // If GST is not included, we add it to the base amount
      const gstAmount = (baseAmount * gstPercentage) / 100;
      const total = baseAmount + gstAmount;
      return {
        total: total.toFixed(2),
        gstAmount: gstAmount.toFixed(2),
        baseAmount: baseAmount.toFixed(2)
      };
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center">
          <FiSettings className="text-xl sm:text-2xl text-blue-600 mr-3 w-5 h-5 sm:w-6 sm:h-6" />
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Room Settings</h2>
            <p className="text-gray-600 text-sm sm:text-base">Configure GST percentage for room pricing</p>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={updateSettings} className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* GST Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              <FiPercent className="inline mr-2 w-4 h-4" />
              GST Percentage
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"   // mobile pe numeric keyboard ke liye
                value={gstPercentage}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only numbers + decimal
                  if (/^\d*\.?\d*$/.test(value)) {
                    setGstPercentage(value === "" ? "" : Number(value));
                  }
                }}
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="18"
              />
              <span className="absolute right-3 top-2.5 text-gray-500 text-sm">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">GST percentage applied to room charges</p>
          </div>

          {/* GST Include Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              <FiCheck className="inline mr-2 w-4 h-4" />
              GST Include
            </label>
            <div className="flex items-center space-x-2 sm:space-x-3 mt-2 sm:mt-3">
              <button
                type="button"
                onClick={() => setIsGstInclude(!isGstInclude)}
                disabled
                className={`relative inline-flex h-5 sm:h-6 w-10 sm:w-11 items-center rounded-full transition-colors ${
                  isGstInclude ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-3.5 sm:h-4 w-3.5 sm:w-4 transform rounded-full bg-white transition-transform ${
                    isGstInclude ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0.5 sm:translate-x-1'
                  }`}
                />
              </button>
              <span className="text-xs sm:text-sm text-gray-600">
                {isGstInclude ? 'GST included' : 'GST added'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {isGstInclude 
                ? 'Room prices include GST tax' 
                : 'GST will be added to room prices'
              }
            </p>
          </div>
        </div>

        {/* Calculation Preview */}
        <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-gray-50 rounded-lg">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">GST Calculation Preview</h3>
          <div className="space-y-2">
            {isGstInclude ? (
              <>
                {/* When GST is included - show breakdown */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Room Price (Including GST)</span>
                  <span className="font-medium text-sm sm:text-base">₹1000.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST Amount ({gstPercentage}%)</span>
                  <span className="font-medium text-sm sm:text-base">₹{calculateTotalWithGST(1000).gstAmount}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Actual Room Charge</span>
                  <span className="font-medium text-sm sm:text-base">₹{calculateTotalWithGST(1000).baseAmount}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-800 text-sm sm:text-base">Total Amount</span>
                    <span className="font-bold text-blue-600 text-sm sm:text-base">₹{calculateTotalWithGST(1000).total}</span>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                  <strong>Note:</strong> Customer pays ₹1000 which includes GST of ₹{calculateTotalWithGST(1000).gstAmount}. 
                  Your actual room charge is ₹{calculateTotalWithGST(1000).baseAmount}.
                </div>
              </>
            ) : (
              <>
                {/* When GST is NOT included - show addition */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Amount (Room Charge)</span>
                  <span className="font-medium text-sm sm:text-base">₹1000.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">GST ({gstPercentage}%)</span>
                  <span className="font-medium text-sm sm:text-base">₹{calculateTotalWithGST(1000).gstAmount}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-800 text-sm sm:text-base">Total Amount</span>
                    <span className="font-bold text-blue-600 text-sm sm:text-base">₹{calculateTotalWithGST(1000).total}</span>
                  </div>
                </div>
                <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700">
                  <strong>Note:</strong> Customer pays ₹{calculateTotalWithGST(1000).total} (₹1000 + ₹{calculateTotalWithGST(1000).gstAmount} GST).
                </div>
              </>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-4 sm:mt-6">
          <button
            type="submit"
            disabled={saving}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave className="mr-2 w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoomSettings;
